import { apiRequest } from "./apiService";
import { buildQueryString } from "./queryString";

/* ────────────────────────────────────────────────
   Small utilities
───────────────────────────────────────────────── */
const toStr = (value) => String(value == null ? "" : value).trim();

const enc = (value) => encodeURIComponent(toStr(value));

const toUndefinedStr = (value) => {
  const s = toStr(value);
  return s || undefined;
};

const toNullableStr = (value) => {
  const s = toStr(value);
  return s || null;
};

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const firstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const normalizeStatus = (status) => {
  const s = toStr(status).toLowerCase();

  if (!s) return undefined;
  if (["draft", "pending", "new"].includes(s)) return "draft";
  if (["sent", "shared", "delivered"].includes(s)) return "sent";
  if (["accepted", "approved"].includes(s)) return "accepted";
  if (["live", "posted", "published"].includes(s)) return "live";
  if (["completed", "complete", "fulfilled", "closed"].includes(s)) {
    return "completed";
  }
  if (["declined", "rejected", "cancelled", "canceled"].includes(s)) {
    return "declined";
  }

  return s;
};

const normalizeDate = (value) => {
  const s = toStr(value);
  return s || undefined;
};

const normalizeIsoDateTime = (value) => {
  const s = toStr(value);
  return s || undefined;
};

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(v)) return true;
    if (["false", "0", "no", "n"].includes(v)) return false;
  }

  return undefined;
};

const isSharedStatus = (status) => {
  const s = normalizeStatus(status);
  return ["sent", "accepted", "live", "completed", "declined"].includes(s);
};

const deriveSharedValue = ({ shared, sharedAt, status }) => {
  const explicitShared = normalizeBoolean(shared);
  if (explicitShared !== undefined) return explicitShared;

  if (toStr(sharedAt)) return true;
  if (isSharedStatus(status)) return true;

  return false;
};

const normalizeStringList = (value) => {
  if (!Array.isArray(value)) return [];

  const seen = new Set();
  const out = [];

  value.forEach((item) => {
    const text = toStr(item);
    if (!text || seen.has(text)) return;
    seen.add(text);
    out.push(text);
  });

  return out;
};

const normalizeObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value;
};

/* ────────────────────────────────────────────────
   Response alias mapping
───────────────────────────────────────────────── */
const mapInviteAliases = (raw = {}) => {
  const source =
    raw?.invite && typeof raw.invite === "object"
      ? raw.invite
      : raw?.item && typeof raw.item === "object"
        ? raw.item
        : raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
          ? raw.data
          : raw || {};

  const links = normalizeObject(source.links) || {};
  const milestones = normalizeObject(source.milestones) || {};

  return {
    ...source,

    inviteId: firstDefined(source.inviteId, source.id, source._id),
    id: firstDefined(source.id, source.inviteId, source._id),

    offerId: firstDefined(source.offerId, source.offer_id),
    campaignId: firstDefined(source.campaignId, source.campaign_id),

    creatorId: firstDefined(
      source.creatorId,
      source.creator_id,
      source.influencerId,
      source.influencer_id
    ),
    influencerId: firstDefined(
      source.influencerId,
      source.influencer_id,
      source.creatorId,
      source.creator_id
    ),

    handle: firstDefined(source.handle, source.username),
    name: firstDefined(source.name, source.fullName, source.full_name),

    status: firstDefined(source.status, source.inviteStatus, source.invite_status),
    currentStatus: firstDefined(
      source.currentStatus,
      source.current_status,
      source.status,
      source.inviteStatus,
      source.invite_status
    ),

    shared: firstDefined(source.shared, source.isShared, source.is_shared),
    sharedAt: firstDefined(
      source.sharedAt,
      source.shared_at,
      milestones.sharedAt,
      milestones.shared_at
    ),

    createdAt: firstDefined(source.createdAt, source.created_at),
    updatedAt: firstDefined(source.updatedAt, source.updated_at),

    deadline: firstDefined(source.deadline, source.deadlineDate, source.deadline_date),
    deliverables: firstDefined(source.deliverables),
    compensation: firstDefined(source.compensation),
    paymentTerms: firstDefined(source.paymentTerms, source.payment_terms),
    notes: firstDefined(source.notes),
    dmText: firstDefined(source.dmText, source.dm_text),

    sentAt: firstDefined(
      source.sentAt,
      source.sent_at,
      milestones.sentAt,
      milestones.sent_at,
      source.sharedAt,
      source.shared_at
    ),
    acceptedAt: firstDefined(
      source.acceptedAt,
      source.accepted_at,
      milestones.acceptedAt,
      milestones.accepted_at
    ),
    liveAt: firstDefined(
      source.liveAt,
      source.live_at,
      milestones.liveAt,
      milestones.live_at
    ),
    completedAt: firstDefined(
      source.completedAt,
      source.completed_at,
      milestones.completedAt,
      milestones.completed_at
    ),
    declinedAt: firstDefined(
      source.declinedAt,
      source.declined_at,
      milestones.declinedAt,
      milestones.declined_at
    ),

    trackingUrl: firstDefined(
      source.trackingUrl,
      source.trackingLink,
      source.tracking_url,
      source.tracking_link,
      links.tracking
    ),
    trackingLink: firstDefined(
      source.trackingLink,
      source.trackingUrl,
      source.tracking_link,
      source.tracking_url,
      links.tracking
    ),
    publicInviteUrl: firstDefined(
      source.publicInviteUrl,
      source.public_invite_url,
      links.publicInvite
    ),
    creatorOnboardingLink: firstDefined(
      source.creatorOnboardingLink,
      source.creator_onboarding_link,
      links.creatorOnboarding
    ),

    onboardingStatus: firstDefined(
      source.onboardingStatus,
      source.onboarding_status
    ),
    onboardingSubmittedAt: firstDefined(
      source.onboardingSubmittedAt,
      source.onboarding_submitted_at
    ),
    onboarding: firstDefined(source.onboarding, source.details),

    links,
    milestones,
    availableActions: Array.isArray(source.availableActions)
      ? source.availableActions
      : [],
  };
};

/* ────────────────────────────────────────────────
   Request normalization
───────────────────────────────────────────────── */
const normalizeListParams = (params = {}) => {
  const p = { ...(params || {}) };

  const cursor =
    p.cursor ??
    p.next_cursor ??
    p.nextCursor ??
    p.last_key ??
    p.lastKey ??
    null;

  if (cursor != null && toStr(cursor) !== "") {
    p.cursor = cursor;
  } else {
    delete p.cursor;
  }

  delete p.next_cursor;
  delete p.nextCursor;
  delete p.last_key;
  delete p.lastKey;

  if (p.status != null) {
    const status = normalizeStatus(p.status);
    if (status) {
      p.status = status;
    } else {
      delete p.status;
    }
  }

  if (typeof p.q === "string") {
    p.q = p.q.trim();
    if (!p.q) delete p.q;
  }

  if (p.limit != null) {
    const limit = toNum(p.limit, undefined);
    if (Number.isFinite(limit) && limit > 0) {
      p.limit = limit;
    } else {
      delete p.limit;
    }
  }

  return p;
};

const normalizeInvitePayload = (payload = {}) => {
  const p = { ...(payload || {}) };

  if (p.creatorId != null && p.influencerId == null) {
    p.influencerId = p.creatorId;
  }

  if (p.inviteStatus != null && p.status == null) {
    p.status = p.inviteStatus;
  }

  if (p.trackingLink != null && p.trackingUrl == null) {
    p.trackingUrl = p.trackingLink;
  }

  if (p.payment_terms != null && p.paymentTerms == null) {
    p.paymentTerms = p.payment_terms;
  }

  if (p.shared_at != null && p.sharedAt == null) {
    p.sharedAt = p.shared_at;
  }

  if (p.dm_text != null && p.dmText == null) {
    p.dmText = p.dm_text;
  }

  if (p.public_invite_url != null && p.publicInviteUrl == null) {
    p.publicInviteUrl = p.public_invite_url;
  }

  if (p.creator_onboarding_link != null && p.creatorOnboardingLink == null) {
    p.creatorOnboardingLink = p.creator_onboarding_link;
  }

  const normalizedShared =
    p.shared != null ? normalizeBoolean(p.shared) : undefined;

  const out = {
    influencerId: toUndefinedStr(p.influencerId),
    handle: toUndefinedStr(p.handle),
    deliverables: toUndefinedStr(p.deliverables),
    compensation: toUndefinedStr(p.compensation),
    paymentTerms: toUndefinedStr(p.paymentTerms),
    deadline: normalizeDate(p.deadline),
    notes: toUndefinedStr(p.notes),
    status: normalizeStatus(p.status),
    shared: normalizedShared,
    sharedAt: p.sharedAt != null ? normalizeIsoDateTime(p.sharedAt) : undefined,
    sentAt: p.sentAt != null ? normalizeIsoDateTime(p.sentAt) : undefined,
    acceptedAt:
      p.acceptedAt != null ? normalizeIsoDateTime(p.acceptedAt) : undefined,
    liveAt: p.liveAt != null ? normalizeIsoDateTime(p.liveAt) : undefined,
    completedAt:
      p.completedAt != null ? normalizeIsoDateTime(p.completedAt) : undefined,
    declinedAt:
      p.declinedAt != null ? normalizeIsoDateTime(p.declinedAt) : undefined,
    dmText: toUndefinedStr(p.dmText),
    clientToken: toUndefinedStr(p.clientToken),
  };

  Object.keys(out).forEach((key) => {
    if (out[key] === undefined) delete out[key];
  });

  return out;
};

/* ────────────────────────────────────────────────
   Response normalization
───────────────────────────────────────────────── */
const normalizeInvite = (raw = {}) => {
  const invite = mapInviteAliases(raw);

  const status = normalizeStatus(invite.status) || "draft";
  const currentStatus =
    normalizeStatus(invite.currentStatus || invite.status) || status;

  const sharedAt = toNullableStr(invite.sharedAt || invite.sentAt);
  const sentAt = toNullableStr(invite.sentAt || invite.sharedAt);
  const acceptedAt = toNullableStr(invite.acceptedAt);
  const liveAt = toNullableStr(invite.liveAt);
  const completedAt = toNullableStr(invite.completedAt);
  const declinedAt = toNullableStr(invite.declinedAt);

  const shared = deriveSharedValue({
    shared: invite.shared,
    sharedAt,
    status: currentStatus,
  });

  const trackingUrl = toStr(invite.trackingUrl || invite.trackingLink);
  const publicInviteUrl = toStr(invite.publicInviteUrl);
  const creatorOnboardingLink = toStr(invite.creatorOnboardingLink);

  const milestones = {
    ...(normalizeObject(invite.milestones) || {}),
    sentAt: sentAt || "",
    acceptedAt: acceptedAt || "",
    liveAt: liveAt || "",
    completedAt: completedAt || "",
    declinedAt: declinedAt || "",
  };

  const links = {
    ...(normalizeObject(invite.links) || {}),
    tracking: trackingUrl || "",
    publicInvite: publicInviteUrl || "",
    creatorOnboarding: creatorOnboardingLink || "",
  };

  return {
    ...invite,
    inviteId: toStr(invite.inviteId),
    id: toStr(invite.id || invite.inviteId),
    offerId: toStr(invite.offerId),
    campaignId: toNullableStr(invite.campaignId),
    creatorId: toNullableStr(invite.creatorId),
    influencerId: toNullableStr(invite.influencerId),
    handle: toStr(invite.handle),
    name: toStr(invite.name),
    status,
    currentStatus,
    shared,
    isShared: shared,
    sharedAt,
    shared_at: sharedAt,
    sentAt,
    acceptedAt,
    liveAt,
    completedAt,
    declinedAt,
    createdAt: toStr(invite.createdAt),
    updatedAt: toNullableStr(invite.updatedAt),
    deadline: toStr(invite.deadline),
    deliverables: toStr(invite.deliverables),
    compensation: toStr(invite.compensation),
    paymentTerms: toStr(invite.paymentTerms),
    notes: toStr(invite.notes),
    dmText: toStr(invite.dmText),
    trackingUrl,
    trackingLink: trackingUrl,
    publicInviteUrl,
    creatorOnboardingLink,
    onboardingStatus: toStr(invite.onboardingStatus),
    onboardingSubmittedAt: toNullableStr(invite.onboardingSubmittedAt),
    onboarding:
      invite.onboarding && typeof invite.onboarding === "object"
        ? invite.onboarding
        : null,
    links,
    milestones,
    availableActions: normalizeStringList(invite.availableActions),
  };
};

const normalizeInvitePatchResponse = (raw = {}) => {
  const invite = mapInviteAliases(raw);
  const out = {};

  if (invite.inviteId != null || invite.id != null) {
    out.inviteId = toStr(invite.inviteId || invite.id);
    out.id = toStr(invite.id || invite.inviteId);
  }

  if (invite.offerId != null) out.offerId = toStr(invite.offerId);
  if (invite.campaignId != null) out.campaignId = toNullableStr(invite.campaignId);
  if (invite.creatorId != null) out.creatorId = toNullableStr(invite.creatorId);
  if (invite.influencerId != null) {
    out.influencerId = toNullableStr(invite.influencerId);
  }

  if (invite.handle != null) out.handle = toStr(invite.handle);
  if (invite.name != null) out.name = toStr(invite.name);

  if (invite.status != null) {
    out.status = normalizeStatus(invite.status) || toStr(invite.status);
  }

  if (invite.currentStatus != null || invite.status != null) {
    out.currentStatus =
      normalizeStatus(invite.currentStatus || invite.status) ||
      toStr(invite.currentStatus || invite.status);
  }

  if (
    invite.shared != null ||
    invite.sharedAt != null ||
    invite.sentAt != null ||
    invite.currentStatus != null ||
    invite.status != null
  ) {
    const shared = deriveSharedValue({
      shared: invite.shared,
      sharedAt: invite.sharedAt || invite.sentAt,
      status: invite.currentStatus || invite.status,
    });
    out.shared = shared;
    out.isShared = shared;
  }

  if (invite.sharedAt != null || invite.sentAt != null) {
    const value = toNullableStr(invite.sharedAt || invite.sentAt);
    out.sharedAt = value;
    out.shared_at = value;
  }

  if (invite.sentAt != null) out.sentAt = toNullableStr(invite.sentAt);
  if (invite.acceptedAt != null) out.acceptedAt = toNullableStr(invite.acceptedAt);
  if (invite.liveAt != null) out.liveAt = toNullableStr(invite.liveAt);
  if (invite.completedAt != null) {
    out.completedAt = toNullableStr(invite.completedAt);
  }
  if (invite.declinedAt != null) out.declinedAt = toNullableStr(invite.declinedAt);

  if (invite.createdAt != null) out.createdAt = toStr(invite.createdAt);
  if (invite.updatedAt != null) out.updatedAt = toNullableStr(invite.updatedAt);

  if (invite.deadline != null) out.deadline = toStr(invite.deadline);
  if (invite.deliverables != null) out.deliverables = toStr(invite.deliverables);
  if (invite.compensation != null) out.compensation = toStr(invite.compensation);
  if (invite.paymentTerms != null) out.paymentTerms = toStr(invite.paymentTerms);
  if (invite.notes != null) out.notes = toStr(invite.notes);
  if (invite.dmText != null) out.dmText = toStr(invite.dmText);

  if (invite.trackingUrl != null || invite.trackingLink != null) {
    const trackingUrl = toStr(invite.trackingUrl || invite.trackingLink);
    out.trackingUrl = trackingUrl;
    out.trackingLink = trackingUrl;
  }

  if (invite.publicInviteUrl != null) {
    out.publicInviteUrl = toStr(invite.publicInviteUrl);
  }

  if (invite.creatorOnboardingLink != null) {
    out.creatorOnboardingLink = toStr(invite.creatorOnboardingLink);
  }

  if (invite.onboardingStatus != null) {
    out.onboardingStatus = toStr(invite.onboardingStatus);
  }

  if (invite.onboardingSubmittedAt != null) {
    out.onboardingSubmittedAt = toNullableStr(invite.onboardingSubmittedAt);
  }

  if (invite.onboarding && typeof invite.onboarding === "object") {
    out.onboarding = invite.onboarding;
  }

  if (invite.availableActions != null) {
    out.availableActions = normalizeStringList(invite.availableActions);
  }

  const trackingUrl = toStr(out.trackingUrl || "");
  const publicInviteUrl = toStr(out.publicInviteUrl || "");
  const creatorOnboardingLink = toStr(out.creatorOnboardingLink || "");

  if (
    trackingUrl ||
    publicInviteUrl ||
    creatorOnboardingLink ||
    invite.links ||
    invite.milestones
  ) {
    out.links = {
      ...(normalizeObject(invite.links) || {}),
      tracking: trackingUrl || "",
      publicInvite: publicInviteUrl || "",
      creatorOnboarding: creatorOnboardingLink || "",
    };

    out.milestones = {
      ...(normalizeObject(invite.milestones) || {}),
      sentAt: toStr(out.sentAt || out.sharedAt || ""),
      acceptedAt: toStr(out.acceptedAt || ""),
      liveAt: toStr(out.liveAt || ""),
      completedAt: toStr(out.completedAt || ""),
      declinedAt: toStr(out.declinedAt || ""),
    };
  }

  return out;
};

const normalizeInviteListResponse = (raw = {}) => {
  const source =
    raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw || {};

  const rawItems = Array.isArray(source.items)
    ? source.items
    : Array.isArray(source.invites)
      ? source.invites
      : Array.isArray(source.data)
        ? source.data
        : [];

  const nextCursor =
    source.next_cursor ??
    source.nextCursor ??
    source.cursor ??
    null;

  const items = rawItems.map(normalizeInvite);

  return {
    ...source,
    items,
    count: toNum(source.count, items.length),
    next_cursor: nextCursor,
    nextCursor,
    statusSummary:
      source.statusSummary && typeof source.statusSummary === "object"
        ? source.statusSummary
        : {},
    currentStatusSummary:
      source.currentStatusSummary && typeof source.currentStatusSummary === "object"
        ? source.currentStatusSummary
        : {},
  };
};

/* ────────────────────────────────────────────────
   Public API
───────────────────────────────────────────────── */

/**
 * GET /offers/{offerId}/invites
 */
export const listOfferInvites = async (offerId, params = {}) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required to list invites.");
  }

  const qs = buildQueryString(normalizeListParams(params));
  const res = await apiRequest(`/offers/${enc(offerId)}/invites${qs}`, "GET");

  return normalizeInviteListResponse(res || {});
};

/**
 * POST /offers/{offerId}/invites
 */
export const createOfferInvite = async (offerId, payload) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required to create an invite.");
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Valid payload is required to create an invite.");
  }

  const res = await apiRequest(
    `/offers/${enc(offerId)}/invites`,
    "POST",
    normalizeInvitePayload(payload)
  );

  return normalizeInvite(res || {});
};

/**
 * Resolve from the offer invite list.
 */
export const getOfferInvite = async (offerId, inviteId, params = {}) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  if (!toStr(inviteId)) {
    throw new Error("inviteId is required.");
  }

  const res = await listOfferInvites(offerId, {
    limit: params.limit || 200,
    cursor: params.cursor,
    status: params.status,
    q: params.q,
  });

  const wantedId = toStr(inviteId);

  const found = Array.isArray(res?.items)
    ? res.items.find((item) => toStr(item.inviteId) === wantedId)
    : null;

  if (!found) {
    throw new Error("Invite not found.");
  }

  return normalizeInvite(found);
};

/**
 * PATCH /offers/{offerId}/invites/{inviteId}
 */
export const patchInvite = async (offerId, inviteId, payload) => {
  if (!toStr(offerId)) {
    throw new Error("offerId is required.");
  }

  if (!toStr(inviteId)) {
    throw new Error("inviteId is required.");
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Valid payload is required to update an invite.");
  }

  const normalizedPayload = normalizeInvitePayload(payload);

  const res = await apiRequest(
    `/offers/${enc(offerId)}/invites/${enc(inviteId)}`,
    "PATCH",
    normalizedPayload
  );

  if (!res || (typeof res === "object" && Object.keys(res).length === 0)) {
    return normalizeInvitePatchResponse({
      inviteId,
      offerId,
      ...normalizedPayload,
    });
  }

  return normalizeInvitePatchResponse(res || {});
};

/**
 * Backward-compatible alias.
 */
export const patchOfferInvite = async (offerId, inviteId, payload) => {
  return patchInvite(offerId, inviteId, payload);
};

/**
 * Convenience helper for marking an invite as shared.
 */
export const markInviteShared = async (
  offerId,
  inviteId,
  sharedAt = new Date().toISOString()
) => {
  return patchInvite(offerId, inviteId, {
    shared: true,
    sharedAt,
  });
};

/**
 * Safe merge helper for partial PATCH responses.
 */
export const mergeInvite = (currentInvite = {}, patchResponse = {}) => {
  return normalizeInvite({
    ...(currentInvite || {}),
    ...(patchResponse || {}),
  });
};